import { useState } from "react";
import { Link2, Loader2, RefreshCcw, Unlink } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLinkDealAccount } from "./use-link-deal-account";
import { useUnlinkDealAccount } from "./use-unlink-deal-account";

// SPA port of LinkAccountButton. Tri-mode account affordance — `link`
// surfaces a primary CTA + dialog, `relink` is a ghost button + pre-filled
// dialog, `unlink` is a destructive AlertDialog. Replaces the legacy
// `useTransition` + server-action pattern with the Phase 6 mutation hooks.

type Props =
  | { hubspotDealId: string; mode: "link" }
  | { hubspotDealId: string; mode: "relink"; currentHubspotId: string }
  | { hubspotDealId: string; mode: "unlink" };

export function LinkAccountButton(props: Props) {
  const { hubspotDealId, mode } = props;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    mode === "relink" ? props.currentHubspotId : "",
  );
  const linkMutation = useLinkDealAccount();
  const unlinkMutation = useUnlinkDealAccount();

  if (mode === "unlink") {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            type="button"
            disabled={unlinkMutation.isPending}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {unlinkMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Unlink className="h-3 w-3" />
            )}
            Unlink
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink this account?</AlertDialogTitle>
            <AlertDialogDescription>
              The deal will no longer show a target customer. The cached
              account snapshot stays in the database for analytics — re-link
              any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlinkMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={unlinkMutation.isPending}
              onClick={() => unlinkMutation.mutate({ hubspotDealId })}
            >
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const id = value.trim();
    if (!id) return;
    linkMutation.mutate(
      { hubspotDealId, hubspotCompanyId: id },
      {
        onSuccess: () => setOpen(false),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {mode === "link" ? (
          <Button size="sm" variant="outline">
            <Link2 className="mr-1.5 h-3 w-3" />
            Link an account
          </Button>
        ) : (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]">
            <RefreshCcw className="mr-1 h-3 w-3" />
            Change
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {mode === "link" ? "Link an account" : "Change linked account"}
          </DialogTitle>
          <DialogDescription>
            Paste the HubSpot Company ID for the target customer. We&apos;ll
            pull the latest snapshot from HubSpot.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="hubspot_company_id" className="text-xs">
              HubSpot Company ID
            </Label>
            <Input
              id="hubspot_company_id"
              name="hubspot_company_id"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. 12345678901"
              autoFocus
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={linkMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={linkMutation.isPending}>
              {linkMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Link
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
